import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CircularProgress, useTheme } from '@mui/material'
import { useIntl } from 'react-intl'

import AppsDeleteDialog, { AppsDeleteDialogRefProps } from 'features/apps/ui/AppsCard/AppsDeleteDialog'
import AccountAppsRegisteredFormDialog, {
  AccountAppsRegisteredFormDialogRefProps
} from './AccountAppsRegisteredFormDialog'

import { ContextMenuIcon } from 'features/common/ui/ContextMenu'
import MoreIcon from 'assets/icons/icon-navigation-more-vert.svg?react'
import { App, AppTypes } from 'features/apps/types'
import { useAppSelector } from 'app/state'
import useNotifications from 'features/notifications/ui/useNotifications'
import { RequestStatus } from 'features/common'
import { useNavigate } from 'react-router-dom'
import AppsShareDialog from 'features/apps/ui/AppForm/AppShareDialog/AppShareDialog'
import { useSaveApp } from 'features/apps/hooks/appsHooks'

interface AccountAppsMenuWrapperProps {
  app: App
}

function AccountAppsMenuWrapper({ app }: AccountAppsMenuWrapperProps) {
  const navigate = useNavigate()
  const theme = useTheme()
  const intl = useIntl()
  const { mutateAsync: updateApp, isLoading: isUpdating } = useSaveApp()
  const deleteDialogRef = useRef<AppsDeleteDialogRefProps>(null)
  const appFormRef = useRef<AccountAppsRegisteredFormDialogRefProps>(null)
  const saveAppStatus = useAppSelector((state) => state.apps.saveAppStatus)
  const saveAppError = useAppSelector((state) => state.apps.saveAppError)
  const [showSharing, setShowSharing] = useState(false)
  const { sendErrorNotification } = useNotifications()

  const togglePinApp = useCallback(async () => {
    const payload = { ...app, bookmarked: !app.bookmarked, type: AppTypes.Listed, id: app.id }
    await updateApp(payload)
  }, [app, updateApp])

  const onClickMenuItem = useCallback(
    ({ id }) => {
      switch (id) {
        case 'edit':
          navigate(`${app.id}`)
          break
        case 'pin':
          togglePinApp()
          break
        case 'delete':
          if (null !== deleteDialogRef.current) {
            deleteDialogRef.current.show()
          }
          break
        case 'share':
          setShowSharing(true)
          break
        default:
      }
    },
    [deleteDialogRef, navigate, togglePinApp, app.id]
  )

  useEffect(() => {
    if (saveAppStatus === RequestStatus.Rejected && saveAppError) {
      sendErrorNotification(saveAppError.error)
    }
  }, [saveAppStatus, sendErrorNotification, saveAppError])

  const actions = useMemo(
    () => [
      {
        id: 'edit',
        label: intl.formatMessage({ id: 'apps.cards.actions.edit' })
      },
      {
        id: 'pin',
        label: app.bookmarked
          ? intl.formatMessage({ id: 'apps.cards.actions.unpin' })
          : intl.formatMessage({ id: 'apps.cards.actions.pin' })
      },
      {
        id: 'share',
        label: intl.formatMessage({ id: 'apps.cards.actions.share' })
      },
      {
        id: 'delete',
        label: intl.formatMessage({ id: 'apps.cards.actions.delete' }),
        destructive: true
      }
    ],
    [app.bookmarked, intl]
  )

  if (isUpdating) {
    return <CircularProgress size={16} />
  }

  return (
    <>
      <ContextMenuIcon
        aria-label="App actions"
        menuItems={actions}
        placement="bottom-end"
        onClickItem={onClickMenuItem}
        alignRight={true}
      >
        <MoreIcon fill={theme.palette.text.secondary} />
      </ContextMenuIcon>
      <AppsDeleteDialog ref={deleteDialogRef} app={app} />
      <AccountAppsRegisteredFormDialog ref={appFormRef} app={app} />
      <AppsShareDialog app={app} open={showSharing} onClose={() => setShowSharing(false)} />
    </>
  )
}

export default AccountAppsMenuWrapper
